import { z } from 'zod';
import { AddressSchema, BusinessDetailsSchema, type Address } from '../backend_config.types';
import { JsonValueSchema } from './common.schemas';
import {
    CardTypeSchema,
    TransactionDetailsSchema,
    TokenizationResultSchema,
} from './payment.schemas';
import { GatewayLifecycleRequestSchema } from './ipg/gateway-lifecycle-request.schemas';
import {
    type GetReceiptDataResponse,
    type ProcessAchPaymentRequest,
    type ProcessAchPaymentResponse,
    type ProcessCardTokenPaymentResponse,
    type TerminalAbortResponse,
    type TerminalSaleResponse,
    type TransactionDetails,
    type TransactionDetailsCreateRequest,
    type PaymentTransactionAmountBreakdown,
} from '../payment.types';

const UuidSchema: z.ZodString = z.string().uuid();

export const CardholderBillingAddressSchema: z.ZodType<Address> = AddressSchema.superRefine(
    (value: Address, ctx: z.RefinementCtx) => {
        if (value.street.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing street is required',
                path: ['street'],
            });
        }
        if (value.city.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing city is required',
                path: ['city'],
            });
        }
        if (value.country === 'US' && value.zip === '00000') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing ZIP must not be 00000',
                path: ['zip'],
            });
        }
    }
);

export const BillingAddressSchema: z.ZodType<Address> = CardholderBillingAddressSchema;

export const SambaGatewayResponseHeaderSchema: z.ZodType<Record<string, string>> =
    z.record(z.string(), z.string());

const TransactionDetailsCreateRequestSchema: z.ZodType<
    TransactionDetailsCreateRequest<TransactionDetails>
> = z.lazy(() => TransactionDetailsSchema);

export const TerminalSaleResponseSchema: z.ZodType<TerminalSaleResponse> = z.object({
    payment_transaction_uuid: UuidSchema.optional(),
    gateway_txn_id: z.string().optional(),
    success: z.boolean(),
    error: z.string().optional(),
    tokenization_result: TokenizationResultSchema.optional(),
});

export const TerminalAbortResponseSchema: z.ZodType<TerminalAbortResponse> = z.object({
    success: z.boolean(),
    error: z.string().optional(),
});

export const ProcessCardTokenPaymentResponseSchema:
    z.ZodType<ProcessCardTokenPaymentResponse> = z.object({
        payment_transaction_uuid: UuidSchema.optional(),
        gateway_txn_id: z.string().optional(),
        success: z.boolean(),
        error: z.string().optional(),
    });

export const ProcessAchPaymentRequestSchema: z.ZodType<ProcessAchPaymentRequest> =
    z.object({
        transaction_details: TransactionDetailsCreateRequestSchema,
        gateway_txn_id: z.string().optional(),
        gateway_request: GatewayLifecycleRequestSchema.optional(),
        gateway_response: JsonValueSchema.optional(),
        gateway_response_status: z.number().int().optional(),
        gateway_response_header: SambaGatewayResponseHeaderSchema.optional(),
    });

export const ProcessAchPaymentResponseSchema: z.ZodType<ProcessAchPaymentResponse> =
    z.object({
        success: z.boolean(),
        payment_transaction_uuid: UuidSchema,
    });

export const PaymentTransactionAmountBreakdownSchema:
    z.ZodType<PaymentTransactionAmountBreakdown> = z
        .object({
            base_amount: z.number(),
            surcharge: z.number().optional(),
            convenience_fee: z.number().optional(),
            tax: z.number().optional(),
            donation: z.number().optional(),
            total_amount: z.number().optional(),
        })
        .transform((value): PaymentTransactionAmountBreakdown => ({
            ...value,
            total_amount:
                value.total_amount ??
                Number(
                    (
                        value.base_amount +
                        (value.surcharge ?? 0) +
                        (value.convenience_fee ?? 0) +
                        (value.donation ?? 0) +
                        (value.tax ?? 0)
                    ).toFixed(2)
                ),
        }));

export const GetReceiptDataResponseSchema: z.ZodType<GetReceiptDataResponse> =
    z.object({
        merchant_transaction_id: z.string(),
        receipt_timestamp: z.string(),
        gateway_rrn: z.string().optional(),
        gateway_auth_code: z.string().optional(),
        card_brand: z.string().optional(),
        card_type: CardTypeSchema,
        card_last4: z.string().optional(),
        surcharge_pct: z.number().optional(),
        original_amount: z.string(),
        amount_breakdown: PaymentTransactionAmountBreakdownSchema,
        transactionDetails: z.lazy(() => TransactionDetailsSchema),
        business_details: BusinessDetailsSchema.optional(),
        can_get_receipt: z.boolean(),
        missing_required_fields: z.array(z.string()),
    });
